/**
 * Name formatting utilities
 */

/**
 * Formats a full name from name components
 * @param namePrefix - The name prefix (e.g., "Dr.", "Mr.", "Ms.")
 * @param firstName - The first name
 * @param middleName - The middle name (optional)
 * @param lastName - The last name
 * @returns Formatted full name string
 */
export const formatFullName = (
  namePrefix?: string | null,
  firstName?: string | null,
  middleName?: string | null,
  lastName?: string | null
): string => {
  const parts = [namePrefix, firstName, middleName, lastName].filter(Boolean);
  return parts.join(" ");
};

/**
 * Formats a full name from a user object with name components
 * @param user - User object with name properties
 * @returns Formatted full name string
 */
export const formatUserFullName = (user: {
  namePrefix?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): string => {
  return formatFullName(
    user.namePrefix,
    user.firstName,
    user.middleName,
    user.lastName
  );
};

/**
 * Gets initials from a name
 * @param firstName - The first name
 * @param lastName - The last name
 * @returns Initials string (e.g., "JD" for "John Doe")
 */
export const getInitials = (
  firstName?: string | null,
  lastName?: string | null
): string => {
  const firstInitial = firstName?.[0]?.toUpperCase() || "";
  const lastInitial = lastName?.[0]?.toUpperCase() || "";
  return firstInitial + lastInitial;
};

/**
 * Gets initials from a user object
 * @param user - User object with name properties
 * @returns Initials string
 */
export const getUserInitials = (user: {
  firstName?: string | null;
  lastName?: string | null;
}): string => {
  return getInitials(user.firstName, user.lastName);
};

/**
 * Formats a display name (first name + last name, no prefix)
 * @param firstName - The first name
 * @param lastName - The last name
 * @returns Display name string
 */
export const formatDisplayName = (
  firstName?: string | null,
  lastName?: string | null
): string => {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.join(" ");
};

/**
 * Formats a formal name (prefix + last name)
 * @param namePrefix - The name prefix
 * @param lastName - The last name
 * @returns Formal name string
 */
export const formatFormalName = (
  namePrefix?: string | null,
  lastName?: string | null
): string => {
  const parts = [namePrefix, lastName].filter(Boolean);
  return parts.join(" ");
};

/**
 * Capitalizes the first letter of each word in a name
 * @param name - The name to capitalize
 * @returns Capitalized name string
 */
export const capitalizeName = (name: string): string => {
  return name
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Validates if a name component is valid
 * @param name - The name to validate
 * @returns True if the name is valid
 */
export const isValidName = (name: string): boolean => {
  return name.trim().length > 0 && /^[a-zA-Z\s\-'.]+$/.test(name);
};

/**
 * Truncates a name to a maximum length
 * @param name - The name to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated name string
 */
export const truncateName = (name: string, maxLength: number = 50): string => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 3) + "...";
};
